import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { notify } from 'app/store/common-effects/notifier.effects';
import { navigateToRoot } from 'app/store/common-effects/router.effects';
import { rootProjects } from 'app/store/root/projects/projects.selectors';
import { ProjectOutput } from 'core/http/dev';
import { Role } from 'core/http/lg-logistic-rest-api/Auth/token.service';
import { Invite, UserService } from 'core/http/root';
import { selectFirst } from 'core/utils/rx-common';
import { environment } from 'environments/environment';
import { ClipboardService } from 'ngx-clipboard';
import { map } from 'rxjs/operators';
import { ObserverFactory } from 'shared/common/observers';

type SelectableProject = ProjectOutput & { isSelected: boolean };

@Component({
  selector: 'app-invite',
  templateUrl: './invite.component.html',
  styleUrls: ['./invite.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InviteComponent implements OnInit {
  private _role: Role = 'ROLE_MANAGER';
  private _inviteLinks = new Array<string>();
  private _selectedProjects = new Array<SelectableProject>();

  get selectedProjects(): SelectableProject[] {
    return this._selectedProjects;
  }

  set selectedProjects(value: SelectableProject[]) {
    this._selectedProjects = value;
  }

  get inviteLinks(): string[] {
    return this._inviteLinks;
  }

  get role(): Role {
    return this._role;
  }

  set role(value: Role) {
    this._role = value;
  }

  constructor(
    private userService: UserService,
    private observers: ObserverFactory,
    private clipboardService: ClipboardService,
    private store$: Store,
  ) { }

  ngOnInit(): void {
    this.store$.pipe(selectFirst(rootProjects))
      .subscribe(data => this.selectedProjects = data.map(p => ({ ...p, isSelected: false })));
  }

  generateInvite(): void {
    const projectIds = this.selectedProjects
      .filter(p => p.isSelected)
      .map(p => p.id);

    this.userService
      .invite(this.role, projectIds)
      .pipe(map(i => this.generateLink(i)))
      .subscribe(
        this.observers.withHook({
          successMsg: 'NOTIFICATIONS.link_generated',
          onSuccess: (link: string) => {
            this.inviteLinks.push(link);
            this.resetSelectedProjects();
          },
        }),
      );
  }

  goBack(): void {
    this.store$.dispatch(navigateToRoot({ path: ['managers'] }));
  }

  copyInvite(inviteIdx: number): void {
    this.clipboardService.copyFromContent(this.inviteLinks[inviteIdx]);
    this.store$.dispatch(notify({ message: 'NOTIFICATIONS.link_copied' }));
  }

  resetSelectedProjects(): void {
    this.selectedProjects = this.selectedProjects.map(p => ({ ...p, isSelected: false }));
  }

  generateLink(invite: Invite): string {
    return `${environment.REGISTRATION_URL}/${invite.id}`;
  }
}
